import { useEffect, useState } from "react";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateRangeFilter } from "@/components/admin/DateRangeFilter";
import { DEFAULT_FILTERS, JENIS_OPTIONS, STATUS_OPTIONS, slugify } from "@/lib/constants";
import { cn } from "@/lib/utils";

const JENIS_ITEMS = [{ value: "all", label: "Semua Jenis Pengaduan" }, ...JENIS_OPTIONS.map((j) => ({ value: j.value, label: j.value }))];
const STATUS_ITEMS = [{ value: "all", label: "Semua Status" }, ...STATUS_OPTIONS.map((s) => ({ value: s, label: s }))];

const FilterSelect = ({ value, onChange, items, testId, className }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className={cn("h-10 rounded-xl border-slate-200 bg-white text-sm", className)} data-testid={testId}>
      <SelectValue />
    </SelectTrigger>
    <SelectContent className="bg-white">
      {items.map((item) => (
        <SelectItem key={item.value} value={item.value} data-testid={`${testId}-option-${slugify(item.value)}`}>
          {item.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const SearchBox = ({ value, onChange }) => {
  const [term, setTerm] = useState(value);

  useEffect(() => {
    if (!value) setTerm("");
  }, [value]);

  useEffect(() => {
    const timer = setTimeout(() => onChange((f) => (f.q === term.trim() ? f : { ...f, q: term.trim() })), 400);
    return () => clearTimeout(timer);
  }, [term, onChange]);

  return (
    <div className="relative w-full md:max-w-lg">
      <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <Input
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        placeholder="Cari kata kunci: no. tiket, nama, alamat, detail, no. HP..."
        className="h-11 rounded-xl border-slate-200 bg-white pl-10 text-sm"
        data-testid="admin-search-input"
      />
    </div>
  );
};

export const FiltersBar = ({ filters, onChange, count }) => {
  const active = filters.jenis !== "all" || filters.status !== "all" || !!filters.q || !!filters.from;
  return (
    <div className="space-y-3" data-testid="filters-bar">
      <SearchBox value={filters.q} onChange={onChange} />
      <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center">
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <SlidersHorizontal className="h-4 w-4 text-emerald-600" /> Filter
        </span>
        <FilterSelect value={filters.jenis} onChange={(v) => onChange((f) => ({ ...f, jenis: v }))} items={JENIS_ITEMS} testId="jenis-filter-select" className="md:w-72" />
        <FilterSelect value={filters.status} onChange={(v) => onChange((f) => ({ ...f, status: v }))} items={STATUS_ITEMS} testId="status-filter-select" className="md:w-48" />
        <DateRangeFilter from={filters.from} to={filters.to} onChange={(range) => onChange((f) => ({ ...f, ...range }))} />
        {active && (
          <Button variant="ghost" size="sm" onClick={() => onChange(DEFAULT_FILTERS)} className="self-start text-slate-600 md:self-auto" data-testid="reset-filter-btn">
            <X className="h-4 w-4" /> Reset
          </Button>
        )}
        <p className="text-sm text-slate-500 md:ml-auto" data-testid="result-count">{count} pengaduan ditampilkan</p>
      </div>
    </div>
  );
};
